import React, { useMemo, useState } from "react";

import {
  Box,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { SettingEntity } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/setting_pb";
import { SettingValue } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/settings_pb";

import GenericDialog from "../../shared/generic-dialog";
import { OrgSettingsTableHeader } from "./org-settings-table-header";
import { OrgSettingsTableRow } from "./org-settings-table-row";

type OrgSettingsDialogProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  orgSettings: SettingValue[];
};

export const OrgSettingsDialog = (props: OrgSettingsDialogProps) => {
  const { open, setIsOpen, orgSettings } = props;
  const [showOrgOnly, setShowOrgOnly] = useState(true);

  // Helper function to format entity enum to readable string
  const formatEntity = (entity: SettingEntity): string => {
    switch (entity) {
      case SettingEntity.USER:
        return "User";
      case SettingEntity.ORGANISATION:
        return "Organisation";
      case SettingEntity.FALLBACK:
        return "Fallback";
      case SettingEntity.UNSPECIFIED:
      default:
        return "Unspecified";
    }
  };

  // Helper function to get entity priority for sorting
  const getEntityPriority = (entity: string): number => {
    switch (entity) {
      case "Organisation":
        return 1;
      case "User":
        return 2;
      case "Fallback":
        return 3;
      case "Unspecified":
      default:
        return 4;
    }
  };

  // Transform SettingValue[] to simple {key, value, entity} format for display
  const settings = useMemo(() => {
    const transformed = orgSettings.map((setting) => {
      let value = "";
      if (setting.value?.value.case === "stringValue") {
        value = setting.value.value.value;
      } else if (
        setting.value?.value.case === "numberValue" ||
        setting.value?.value.case === "booleanValue"
      ) {
        value = String(setting.value.value.value);
      }
      return {
        key: setting.key,
        value: value,
        entity: formatEntity(setting.entity),
      };
    });

    // Sort by entity priority (Organisation > User > Fallback > Unspecified)
    const sorted = transformed.sort(
      (a, b) => getEntityPriority(a.entity) - getEntityPriority(b.entity)
    );

    // Filter by Organisation entity if showOrgOnly is true
    if (showOrgOnly) {
      return sorted.filter((setting) => setting.entity === "Organisation");
    }
    return sorted;
  }, [orgSettings, showOrgOnly]);

  return (
    <GenericDialog
      title="Organization Settings"
      width="md"
      hasAckBtn={true}
      acknowledgeBtnLabel="OK"
      open={open}
      handleAckBtnClick={() => {
        setIsOpen(false);
      }}
      handleDialogClose={() => {
        setIsOpen(false);
      }}
      data-testid="org-settings-dialog"
    >
      <TableContainer
        style={{ maxHeight: "600px", overflow: "auto" }}
        component={Paper}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            pb: 0,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showOrgOnly}
                onChange={(e) => setShowOrgOnly(e.target.checked)}
                data-testid="org-settings-org-only-toggle"
              />
            }
            label="Show Organisation Only"
          />
        </Box>
        <Table>
          <TableHead>
            <OrgSettingsTableHeader />
          </TableHead>
          <TableBody>
            {settings.length === 0 && (
              <TableRow data-testid="org-settings-table-empty">
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No Organisation entity settings found. Toggle off option
                    above to view other settings.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {settings.map((setting, index) => (
              <OrgSettingsTableRow setting={setting} key={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </GenericDialog>
  );
};
